import { useEffect, useRef, useState } from "react";

import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useFloating,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
} from "@floating-ui/react";
import { observer } from "mobx-react-lite";

import { Icon } from "@/components/icons/Icon";
import { SuggestionOption } from "@/components/suggestions/list/SuggestionListOption";
import { SuggestionsGroupsList } from "@/components/suggestions/list/SuggestionsGroupsList";
import {
  suggestionOptionId,
  suggestionSubmenuId,
  useSuggestionsListContext,
} from "@/components/suggestions/navigation/SuggestionsListProvider";
import { keyMatcher } from "@/utils/keyMatcher";

import type { SingleSuggestion } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { SuggestionsModel } from "@/models/SuggestionsModel";
import type { Placement } from "@floating-ui/react";

type Props = Omit<React.ComponentProps<"button">, "title"> & {
  doc: DocumentModel;
  suggestions: SuggestionsModel;
  index: number;
  suggestion: SingleSuggestion;
  listId: string | null;
  indicator?: string;
};

export const NestedSuggestionOption = observer(function NestedSuggestionOption({
  ref: forwardedRef,
  doc,
  suggestions,
  index,
  suggestion,
  suggestion: { nestedKey, nestedItems = [] },
  listId,
  indicator,
  ...props
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [allowHover, setAllowHover] = useState(false);

  const isActive = suggestions.isActive(index, listId);
  const { menuId } = useSuggestionsListContext();
  const optionId = suggestionOptionId(menuId, listId, index);
  const isSubmenuShown = (isActive || isOpen) && !!nestedKey;
  const submenuId = nestedKey ? suggestionSubmenuId(menuId, nestedKey) : undefined;

  const { refs, floatingStyles, context } = useFloating({
    nodeId: nestedKey,
    open: isActive || isOpen,
    onOpenChange: setIsOpen,
    placement: "right-start",
    middleware: [offset({ mainAxis: 10, alignmentAxis: -5 }), shift(), flip()],
    whileElementsMounted: autoUpdate,
  });

  const placementRef = useRef<Placement | null>(null);
  placementRef.current = context.placement;

  const hover = useHover(context, {
    enabled: nestedItems.length > 0 ? allowHover : false,
    delay: { open: 75 },
    handleClose: safePolygon({ blockPointerEvents: true }),
  });
  const role = useRole(context, { role: "menu" });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, role]);

  useEffect(() => {
    function onPointerMove({ pointerType }: PointerEvent) {
      if (pointerType !== "touch") {
        setAllowHover(true);
      }
    }

    window.addEventListener("pointermove", onPointerMove, {
      once: true,
      capture: true,
    });

    return () => {
      window.removeEventListener("pointermove", onPointerMove, {
        capture: true,
      });
    };
  }, [allowHover]);

  useEffect(() => {
    if (isActive && allowHover) {
      setIsOpen(true);
    }
  }, [isActive, allowHover]);

  useEffect(() => {
    if (!allowHover && !isActive && suggestions.nestedListId !== nestedKey) {
      setIsOpen(false);
    }
  }, [allowHover, isActive, suggestions.nestedListId, nestedKey]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!placementRef.current || !nestedKey) return;

      setAllowHover(false);

      const isLeftSide = placementRef.current === "left-start";
      const openKey = isLeftSide ? "ArrowLeft" : "ArrowRight";
      const closeKey = isLeftSide ? "ArrowRight" : "ArrowLeft";

      if (isActive && keyMatcher(openKey, e)) {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(true);
        suggestions.setActiveListItem(nestedKey, 0);
        return;
      }

      if (isOpen && keyMatcher(closeKey, e)) {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(false);
        suggestions.setActiveListItem(listId, index);
      }
    };

    document.addEventListener("keydown", handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [nestedKey, listId, isActive, isOpen, index, suggestions]);

  const ref = useMergeRefs([refs.setReference, forwardedRef]);

  return (
    <>
      <SuggestionOption
        ref={ref}
        doc={doc}
        suggestions={suggestions}
        listId={listId}
        index={index}
        suggestion={suggestion}
        isHighlighted={isOpen}
        action={
          <span className="flex items-center justify-end gap-1">
            {indicator}
            <Icon name="ChevronDown" className="-rotate-90" />
          </span>
        }
        {...getReferenceProps({
          ...props,
          onClick(event: React.MouseEvent) {
            event.stopPropagation();
          },
        })}
        // Replaces floating-ui's generated ids, which `aria-activedescendant` and the input's
        // `aria-controls` cannot predict.
        id={optionId}
        aria-controls={isSubmenuShown ? submenuId : undefined}
      />
      {isSubmenuShown && (
        <FloatingPortal>
          <SuggestionsGroupsList
            ref={refs.setFloating}
            doc={doc}
            suggestions={suggestions}
            listId={nestedKey}
            groups={nestedItems}
            style={floatingStyles}
            pseudoFocusIndex={isActive ? 0 : undefined}
            {...getFloatingProps()}
            id={submenuId}
            aria-labelledby={optionId}
          />
        </FloatingPortal>
      )}
    </>
  );
});
